import { FC, memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/_atoms/button";
import { IconUserUnassigned } from "@/components/_icons/IconUserUnassigned";
import { useDatatableContext } from "@/domain/view/components/datatable/use-datatable-context";
import { cn } from "@/lib/cn";

import { useCreateAssignee, UseCreateAssigneeProps } from "../../hooks/api/use-create-assignee";
import { useDeleteAssignee } from "../../hooks/api/use-delete-assignee";
import { useSmartUserList } from "../../hooks/api/use-smart-user-list";
import { Assignee } from "../../models/assignee-model";
import { displayNameForUser } from "../../models/display-name-for-user";
import { User } from "../../models/user-model";
import { AssigneeAvatarGroup } from "../assignee-avatar-group";
import { UserMultiselectDropdown } from "../user-multiselect-dropdown";

type OwnerType = UseCreateAssigneeProps["ownerType"];

interface DatatableMultiAssigneeCellProps {
  assigneeList: Assignee[];
  workspaceId: string;
  ownerId: string;
  ownerType: OwnerType;
  teamIdList: string[];
  editable: boolean;
  className?: string;
}

interface InternalDatatableMultiAssigneeCellProps extends DatatableMultiAssigneeCellProps {
  showColumnHeaders: boolean;
}

const InternalDatatableMultiAssigneeCell: FC<InternalDatatableMultiAssigneeCellProps> = ({
  assigneeList,
  workspaceId,
  ownerId,
  ownerType,
  teamIdList,
  editable,
  showColumnHeaders,
}) => {
  const { t } = useTranslation("user");
  const showAssigneeName = showColumnHeaders;
  const { filterUsers, userList } = useSmartUserList();
  const activeUserList = filterUsers({
    teamMembership: {
      teamIdList: teamIdList,
      status: ["ACTIVE"],
    },
  });

  const resolvedUsers = useMemo(() => {
    return assigneeList
      .map((assignee) => {
        // For public dashboards, user data is embedded in the assignee (no userId)
        // For private dashboards, look up user by userId
        const user = assignee.user ?? (assignee.userId ? userList.find((u) => u.id === assignee.userId) : null);
        return user ?? null;
      })
      .filter((u): u is User => u !== null);
  }, [assigneeList, userList]);
  const { createAssignee } = useCreateAssignee({ ownerType });
  const { deleteAssignee } = useDeleteAssignee();

  const handleValueChange = async (selectedUsers: User[]) => {
    // Filter out assignees without userId (public dashboard context - not editable)
    const currentUserIds = new Set(assigneeList.filter((a) => a.userId).map((a) => a.userId as string));
    const newUserIds = new Set(selectedUsers.map((u) => u.id));

    const toAdd = selectedUsers.filter((u) => !currentUserIds.has(u.id));

    const toRemove = assigneeList.filter((a) => a.userId && !newUserIds.has(a.userId));

    const createPromises = toAdd.map(
      (user) =>
        new Promise<void>((resolve, reject) => {
          createAssignee(
            {
              workspaceId: workspaceId,
              userId: user.id,
              ownerId: ownerId,
              ownerType: ownerType,
            },
            {
              onSuccess: () => resolve(),
              onError: (error: Error) => reject(error),
            },
          );
        }),
    );

    const deletePromises = toRemove.map(
      (assignee) =>
        new Promise<void>((resolve, reject) => {
          deleteAssignee(
            {
              id: assignee.id,
            },
            {
              onSuccess: () => resolve(),
              onError: (error: Error) => reject(error),
            },
          );
        }),
    );

    const allPromises = [...createPromises, ...deletePromises];

    if (allPromises.length === 0) {
      return;
    }

    const results = await Promise.allSettled(allPromises);

    const failures = results.filter((result) => result.status === "rejected");
    const successes = results.filter((result) => result.status === "fulfilled");

    if (failures.length > 0) {
      failures.forEach((failure) => {
        if (failure.status === "rejected") {
          console.error("Assignee mutation failed:", failure.reason);
        }
      });

      const totalMutations = allPromises.length;
      const successCount = successes.length;
      const failureCount = failures.length;

      toast.error(t("assignee.updateError"), { description:
                  failureCount === totalMutations
                    ? t("assignee.allChangesFailed")
                    : t("assignee.partialChangesFailed", { failureCount, totalMutations, successCount }) });
    }
  };

  const renderAvatars = () => {
    if (resolvedUsers.length === 0) {
      return (
        <>
          <div className="h-4.5 w-4.5">
            <IconUserUnassigned className="h-4.5 w-4.5 text-muted-foreground" />
          </div>
          {showAssigneeName && <span className="truncate text-ellipsis whitespace-nowrap">{t("assignee.unassigned")}</span>}
        </>
      );
    }

    return <AssigneeAvatarGroup userList={resolvedUsers} showName={showAssigneeName} />;
  };

  if (!editable) {
    return (
      <div
        className={cn([
          "flex flex-row items-center justify-start gap-1 overflow-x-hidden whitespace-nowrap text-xs font-normal",
          showAssigneeName ? "w-36 max-w-36 truncate text-ellipsis" : "",
        ])}
      >
        {renderAvatars()}
      </div>
    );
  }

  const getAccessibleLabel = (): string => {
    if (resolvedUsers.length === 0) {
      return t("assignee.assign");
    }
    if (resolvedUsers.length === 1) {
      const userName = displayNameForUser(resolvedUsers[0]);
      return t("assignee.changeSingle", { name: userName });
    }
    return t("assignee.changeMultiple", { count: resolvedUsers.length });
  };

  return (
    <UserMultiselectDropdown
      activeUserList={activeUserList}
      value={resolvedUsers}
      onValueChange={handleValueChange}
      side="bottom"
      asChild
    >
      <Button
        variant="ghost"
        size={showAssigneeName ? "xs" : "icon"}
        className={cn([
          "group flex flex-row items-center gap-1 overflow-x-hidden whitespace-nowrap text-xs font-normal ring-offset-background focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus",
          showAssigneeName ? "w-44 max-w-44 truncate text-ellipsis" : "",
        ])}
        aria-label={!showAssigneeName ? getAccessibleLabel() : undefined}
        data-testid="multi-user-select-dropdown-button"
      >
        {renderAvatars()}
      </Button>
    </UserMultiselectDropdown>
  );
};

const areStringListsEqual = (prevList: string[], nextList: string[]): boolean => {
  if (prevList.length !== nextList.length) {
    return false;
  }
  return prevList.every((value, index) => value === nextList[index]);
};

const MemoizedInternalDatatableMultiAssigneeCell = memo(InternalDatatableMultiAssigneeCell, (prevProps, nextProps) => {
  if (prevProps.assigneeList.length !== nextProps.assigneeList.length) {
    return false;
  }

  for (let i = 0; i < prevProps.assigneeList.length; i++) {
    const prev = prevProps.assigneeList[i];
    const next = nextProps.assigneeList[i];
    if (
      prev.id !== next.id ||
      prev.userId !== next.userId ||
      prev.user?.id !== next.user?.id ||
      prev.user?.profilePicture !== next.user?.profilePicture ||
      prev.user?.fullname !== next.user?.fullname ||
      prev.user?.username !== next.user?.username ||
      prev.user?.email !== next.user?.email
    ) {
      return false;
    }
  }

  // teamIdList is rebuilt as a fresh array on every render, so it must be compared by content;
  // a reference check would defeat memoization and re-render every cell in the column.
  return (
    prevProps.workspaceId === nextProps.workspaceId &&
    prevProps.ownerId === nextProps.ownerId &&
    prevProps.ownerType === nextProps.ownerType &&
    areStringListsEqual(prevProps.teamIdList, nextProps.teamIdList) &&
    prevProps.editable === nextProps.editable &&
    prevProps.showColumnHeaders === nextProps.showColumnHeaders
  );
});

export const DatatableMultiAssigneeCell: FC<DatatableMultiAssigneeCellProps> = (props) => {
  const { state } = useDatatableContext();
  const showColumnHeaders = state.showColumnHeaders;

  return <MemoizedInternalDatatableMultiAssigneeCell {...props} showColumnHeaders={showColumnHeaders} />;
};
